/**
 * Quill clipboard override for the community post-composition editors
 * (CreatePostModal, Edit-Post).
 *
 * Word/Google Docs/many web pages represent visual line-spacing as literal
 * empty block elements (<p></p>, <p>&nbsp;</p>, <p><br></p>) or runs of
 * consecutive <br> tags rather than CSS margin. Quill 2's default clipboard
 * matcher unconditionally turns every <p> tag into its own blank line
 * (quill/modules/clipboard.js matchNewline treats HTMLParagraphElement as
 * always line-worthy, regardless of whether it has content), so pasting such
 * content faithfully reproduces every spacer element as its own blank line —
 * several spacer elements in a row read as one huge gap in the editor.
 *
 * Stripping purely-decorative empty spacer elements out of the pasted HTML
 * before Quill converts it keeps every real paragraph break intact while
 * removing the artificial gaps, so pasted text matches the visible/authored
 * content instead of the source markup's spacing artifacts.
 *
 * Importing this module (for its side effect) registers the override once,
 * globally, for every ReactQuill instance in the app.
 */
import Quill from "quill";

const Clipboard = Quill.import("modules/clipboard");

const SPACER_TAGS = "p,div,h1,h2,h3,h4,h5,h6";

const stripPasteSpacers = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");

    doc.body.querySelectorAll(SPACER_TAGS).forEach((el) => {
      if (el.closest("pre, code")) return; // preserve blank lines in code blocks

      const isEmpty =
        !el.querySelector("img, table, hr") &&
        (el.textContent || "").replace(/\s/g, "") === "";

      if (isEmpty) el.remove();
    });

    // Collapse runs of consecutive manual <br> line breaks down to one.
    doc.body.querySelectorAll("br + br").forEach((br) => br.remove());

    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

class SpacerAwareClipboard extends Clipboard {
  onPaste(range, { text, html }) {
    super.onPaste(range, {
      text,
      html: html ? stripPasteSpacers(html) : html,
    });
  }
}

Quill.register("modules/clipboard", SpacerAwareClipboard, true);
